import styled from 'styled-components/macro';
import scaleSize from 'utils/scale-size';
import config from 'config';
import BackButton from 'components/BackButton';
import HorizontalList from 'components/HorizontalList';
import { Metadata, Title } from '../ContentTitle';
import { Focusable, FocusableParent } from 'components/navigation';
import { ReactComponent as PlayerArrowIconUp } from 'assets/img/player-icon-up.svg';
import { ReactComponent as PlayerArrowIconDown } from 'assets/img/player-icon-down.svg';

export const StyledPlayerWrapper = styled.div`
  width: ${({ width }) => scaleSize(width)}px;
  height: ${({ height }) => scaleSize(height)}px;
  top: 0;
  left: 0;
  background: ${({ theme }) => theme.playerBackground};
  position: absolute;
`;

export const StyledVideoPlayer = styled.video`
  width: ${({ width }) => scaleSize(width)}px;
  height: ${({ height }) => scaleSize(height)}px;
`;

export const StyledTizenVideoObject = styled.object`
  width: ${({ width }) => scaleSize(width)}px;
  height: ${({ height }) => scaleSize(height)}px;
`;

export const StyledPlayerHeaderWrapper = styled.div`
  transform: translate3d(0, -${({ idle }) => (idle ? 450 : 0)}px, 0);
  transition: ${config.withAnimation ? `transform ${config.mainTransitionDuration}` : 'none'};
  left: ${scaleSize(136)}px;
  position: relative;
`;

export const StyledChannelItem = styled.div`
  display: none;
  width: ${scaleSize(1500)}px;
  height: ${scaleSize(100)}px;
  margin-top: ${scaleSize(163)}px;
  display: block;
  position: relative;
`;

export const StyledChannelItemLogo = styled.div`
  width: ${scaleSize(132)}px;
  height: ${scaleSize(132)}px;
  background: url(${({ logo }) => logo}) no-repeat center;
  background-color: ${({ theme }) => theme.imagePlaceholder};
  border-radius: ${scaleSize(8)}px;
  background-size: cover;
  display: inline-block;
  margin: 0 ${scaleSize(40)}px 0 0;
`;

export const StyledChannelItemData = styled.div`
  width: ${scaleSize(1250)}px;
  height: ${scaleSize(150)}px;
  display: inline-block;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;

  & > span {
    white-space: nowrap;
    overflow: hidden;
    display: block;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.defaultColor};
    font-size: ${scaleSize(48)}px;
  }

  & > i {
    white-space: nowrap;
    display: block;
    font-style: normal;
    color: ${({ theme }) => theme.defaultColor};
    opacity: 0.7;
    font-size: ${scaleSize(34)}px;
    margin-top: ${scaleSize(4)}px;
    overflow: hidden;
    text-overflow: ellipsis;

    &.white {
      opacity: 1;
    }
  }
`;

export const StyledTitle = styled(Title)`
  width: ${scaleSize(1600)}px;
  font-size: ${scaleSize(48)}px;
  line-height: ${scaleSize(48)}px;
  top: ${scaleSize(33)}px;
  left: 0;
  bottom: auto;
`;

export const StyledMetadata = styled(Metadata)`
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
  top: ${scaleSize(109)}px;
  left: 0;
  & > div > span {
    padding-left: 30px;
  }
`;

export const StyledCatchUpLabel = styled.span`
  color: ${({ theme }) => theme.catchUpLabelColor};
`;

export const StyledControlsWrapper = styled.div`
  width: ${scaleSize(1920)}px;
  height: ${scaleSize(1080)}px;
  position: absolute;
  top: 0;
  left: 0;
  background: ${({ darkOverlay }) => (darkOverlay ? 'rgba(0, 0, 0, 0.5)' : 'none')};

  &:before {
    width: ${scaleSize(1920)}px;
    height: ${scaleSize(1080)}px;
    display: block;
    content: '';
    background: linear-gradient(to top, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0) ${scaleSize(360)}px),
      linear-gradient(to bottom, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0) ${scaleSize(360)}px);
    position: absolute;
    opacity: ${({ idle }) => (idle ? 0 : 1)};
    transition: ${config.withAnimation ? 'opacity 1s' : 'none'};
    top: 0;
    left: 0;
  }
`;

export const StyledBackButton = styled(BackButton)`
  transform: translate3d(-${({ idle }) => (idle ? 300 : 0)}px, 0, 0);
  transition: ${config.withAnimation ? `transform ${config.mainTransitionDuration}` : 'none'};
`;

export const StyledControls = styled.div`
  width: ${scaleSize(1920)}px;
  height: ${({ isSeriesPlayer }) => scaleSize(isSeriesPlayer ? 350 : 300)}px;
  transform: translate3d(0, ${({ idle, isSeriesPlayer }) => (idle ? (isSeriesPlayer ? 530 : 400) : 0)}px, 0);
  transition: ${config.withAnimation ? `transform ${config.mainTransitionDuration}` : 'none'};
  position: absolute;
  bottom: ${({ bottomOffset, isSeriesPlayer }) => scaleSize(bottomOffset || (isSeriesPlayer ? -20 : -90))}px;
`;

export const StyledProgressWrapper = styled(FocusableParent)`
  width: ${({ width }) => width}px;
  height: ${scaleSize(48)}px;
  position: absolute;
  top: -${scaleSize(80)}px;
  left: ${scaleSize(137)}px;
`;

export const StyledProgressFull = styled.div`
  width: ${({ width }) => width}px;
  height: ${scaleSize(12)}px;
  background: ${({ theme }) => theme.playerProgressBackground};
  position: absolute;
  top: ${scaleSize(20)}px;
  border-radius: ${scaleSize(12)}px;
  overflow: hidden;
`;

export const StyledProgressElapsed = styled.div.attrs(({ progress }) => ({
  style: { width: `${scaleSize(progress)}px` },
}))`
  height: ${scaleSize(12)}px;
  background: ${({ theme }) => theme.activeColor};
  position: absolute;
  transform-origin: left;
  top: 0;
  left: 0;
  z-index: 2;
`;

export const StyledLiveProgressElapsed = styled.div.attrs(({ progress }) => ({
  style: { width: `${scaleSize(progress)}px` },
}))`
  height: ${scaleSize(12)}px;
  background: ${({ theme }) => theme.playerProgressElapsed};
  position: absolute;
  border-radius: ${scaleSize(12)}px;
  transform-origin: left;
  top: 0;
  left: 0;
  z-index: 2;
`;

export const StyledCurrentPosition = styled(Focusable).attrs(({ progress }) => ({
  style: { transform: `translate3d(${scaleSize(progress)}px, 0, 0)` },
}))`
  width: ${scaleSize(40)}px;
  height: ${scaleSize(40)}px;
  position: absolute;
  display: block;
  top: ${scaleSize(6)}px;
  margin-left: -${scaleSize(20)}px;
  z-index: 3;
  cursor: pointer;
  background-color: ${({ theme }) => theme.playerCurrentPosition};
  border-radius: 50%;

  &:focus {
    transform: scale(1.2);
    background: ${({ theme }) => theme.activeColor};
  }

  & > span {
    width: ${scaleSize(130)}px;
    height: ${scaleSize(48)}px;
    font-size: ${scaleSize(30)}px;
    line-height: ${scaleSize(48)}px;
    position: absolute;
    left: -${scaleSize(45)}px;
    bottom: -${scaleSize(48)}px;
    display: block;
    text-align: center;
    color: ${({ theme }) => theme.playerCurrentPosition};
    opacity: ${({ isSeeking }) => (isSeeking ? 1 : 0)};
    transition: ${config.withAnimation ? 'opacity 1s' : 'none'};
  }
`;

export const StyledHoveredPosition = styled(StyledCurrentPosition)`
  opacity: 0.5;
`;

export const StyledTimeLabel = styled.div`
  width: ${scaleSize(300)}px;
  height: ${scaleSize(120)}px;
  position: absolute;
  top: 0;
  left: ${scaleSize(120)}px;
  font-family: 'RobotoMedium', sans-serif;
  font-size: ${scaleSize(30)}px;
  line-height: ${scaleSize(120)}px;
  color: ${({ theme }) => theme.playerTime};
  text-align: center;
  opacity: ${({ isSeeking }) => (isSeeking ? 0.5 : 1)};
  transition: ${config.withAnimation ? 'opacity 1s' : 'none'};

  & > span {
    opacity: 0.7;
  }
`;

export const StyledEpisodesTitle = styled.div`
  color: ${({ theme }) => theme.detailsMetadataTextColor};
  height: ${scaleSize(48)}px;
  font-size: ${scaleSize(40)}px;
  line-height: 1.2;
  margin-top: ${scaleSize(21)}px;
  margin-left: ${scaleSize(220)}px;
`;

export const StyledEpisodeslList = styled(HorizontalList)`
  height: ${scaleSize(712)}px;
  transform: translateY(${({ extraOffset }) => extraOffset || 0}px);
  margin-top: ${scaleSize(35)}px;
  margin-left: ${scaleSize(224)}px;
  position: relative;
  white-space: nowrap;
`;

export const StyledPauseLabel = styled.div`
  color: ${({ theme }) => theme.detailsMetadataTextColor};
  font-size: ${scaleSize(34)}px;
  line-height: 1.2;
  position: absolute;
  width: ${scaleSize(150)}px;
  height: ${scaleSize(200)}px;
  left: ${scaleSize(885)}px;
  top: ${scaleSize(435)}px;
  text-align: center;
  & > svg {
    width: ${scaleSize(150)}px;
    height: ${scaleSize(150)}px;
  }
`;

export const StyledOnAirLabel = styled.div`
  position: absolute;
  left: ${scaleSize(205)}px;
  width: auto;
  line-height: ${scaleSize(34)}px;
  font-size: ${scaleSize(24)}px;
  padding: ${scaleSize(15)}px ${scaleSize(20)}px;
  background-color: ${({ theme }) => theme.playerButtonBg};
  border-radius: ${scaleSize(32)}px;
  margin-top: ${scaleSize(30)}px;
  & > svg {
    width: ${scaleSize(20)}px;
    height: ${scaleSize(20)}px;
    margin-right: ${scaleSize(15)}px;
  }
`;

export const StyledIconUp = styled(PlayerArrowIconUp)`
  position: absolute;
  top: -${scaleSize(45)}px;
  width: ${scaleSize(132)}px;
  height: ${scaleSize(62)}px;
`;

export const StyledIconDown = styled(PlayerArrowIconDown)`
  position: absolute;
  top: ${scaleSize(148)}px;
  left: 0;
  width: ${scaleSize(132)}px;
  height: ${scaleSize(62)}px;
`;
